import { Location } from '@angular/common';
import { Component, computed, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import { IonButton, IonButtons, IonIcon, IonMenuButton, IonToolbar } from '@ionic/angular';
import { ApplicationContextStore, RouterService, ThemeService } from '@raptor-fe/services';
import { PermissionKey } from '@raptor-fe/core';
import { addIcons } from 'ionicons';
import { notificationsOutline } from 'ionicons/icons';

/**
 * Configuration for a single header-toolbar action button.
 */
export interface HeaderToolbarButton {
    /** Ionicon name rendered inside the button. */
    icon: string;
    /** Handler invoked when the button is tapped. */
    action: () => void;
    /**
     * Optional permission key, or any-of array of keys, gating the button. When present the button
     * renders only if the current user holds at least one of the keys; when absent the button always
     * renders. Mirrors the {@link HasPermissionDirective} contract for DOM controls.
     */
    permission?: PermissionKey | string | Array<PermissionKey | string>;
}

@Component({
    imports: [IonButton, IonButtons, IonIcon, IonMenuButton, IonToolbar],
    selector: 'lib-header-main-toolbar',
    providers: [RouterService, Location],
    template: `
        <ion-toolbar class="header px-2">
            @if (!isHeaderLogoHidden()) {
                @if (brandingLightLogo() || brandingDarkLogo()) {
                    <img slot="start" [src]="brandingLightLogo()" alt="Logo" class="header-logo-img branding-logo-light" />
                    <img slot="start" [src]="brandingDarkLogo() || brandingLightLogo()" alt="Logo" class="header-logo-img branding-logo-dark" />
                } @else {
                    <div slot="start" class="header-logo"></div>
                }
            }
            <ng-content></ng-content>
            @if (visibleButtons().length > 0 || isShowBackButton()) {
                <ion-buttons slot="end">
                    @if (isShowBackButton()) {
                        <ion-button fill="clear" (click)="goBack()">
                            <ion-icon slot="icon-only" name="arrow-back-outline"></ion-icon>
                        </ion-button>
                    }
                    @for (button of visibleButtons(); track button) {
                        <ion-button fill="clear" (click)="button.action()">
                            <ion-icon slot="icon-only" [name]="button.icon" size="large"></ion-icon>
                        </ion-button>
                    }
                </ion-buttons>
            }
            @if (showNotificationBell()) {
                <ion-buttons slot="end">
                    <ion-button class="notif-btn relative" (click)="notificationClick.emit()">
                        <ion-icon slot="icon-only" name="notifications-outline"></ion-icon>
                        @if (unreadCount() > 0) {
                            <span class="notif-count absolute top-1 right-0 flex h-[18px] w-[18px] items-center justify-center rounded-full text-[11px] font-semibold">{{
                                unreadCount() > 9 ? '9+' : unreadCount()
                            }}</span>
                        }
                    </ion-button>
                </ion-buttons>
            }
            <ion-buttons slot="end">
                <ion-menu-button menu="custom" autoHide="false" class="hamburgerIcon"></ion-menu-button>
            </ion-buttons>
        </ion-toolbar>`,
    styles: [
        `
        .header-logo-img { height: 35px; width: auto; object-fit: contain; }
        .branding-logo-dark { display: none; }
        .branding-logo-light { display: block; }
        @media (prefers-color-scheme: dark) {
            .branding-logo-dark { display: block; }
            .branding-logo-light { display: none; }
        }
        .notif-count {
            background: var(--ion-color-danger);
            color: var(--ion-color-danger-contrast);
        }
    `,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: true,
})
export class HeaderMainToolbarComponent {
    buttons = input<HeaderToolbarButton[]>([]);
    isShowBackButton = input<boolean>(false);
    isHeaderLogoHidden = input<boolean>(false);

    /**
     * Content projected into the toolbar's own row, beside the bell and menu.
     *
     * Additive: a consumer that projects nothing renders exactly as before. It exists so a page whose
     * identity belongs IN the app bar -- a work order's registration and vehicle, say -- can put it
     * there rather than spending a second row underneath, which on a workshop tablet is viewport taken
     * from the work itself. Such a page usually pairs it with {@link isHeaderLogoHidden}.
     */

    /**
     * Opt-in notification bell rendered top-right alongside the hamburger. Off by default so
     * existing consumers render unchanged.
     */
    showNotificationBell = input<boolean>(false);

    /**
     * Unread notification count shown on the bell badge. The badge is hidden when zero.
     */
    unreadCount = input<number>(0);

    /**
     * Emitted when the notification bell is tapped, letting the consumer route to its
     * notifications surface.
     */
    notificationClick = output<void>();

    private readonly themeService = inject(ThemeService);
    protected readonly brandingLightLogo = this.themeService.logoLightUrl;
    protected readonly brandingDarkLogo = this.themeService.logoDarkUrl;
    private readonly location: Location = inject(Location);
    private readonly applicationContextStore = inject(ApplicationContextStore);

    /**
     * The buttons the current user may see: those with no permission, plus those whose permission
     * key(s) the user holds. Recomputes when the buttons input or the user's effective permissions change.
     */
    protected readonly visibleButtons = computed(() =>
        this.buttons().filter(
            (button) =>
                !button.permission ||
                this.applicationContextStore.hasAnyPermission(
                    Array.isArray(button.permission) ? button.permission : [button.permission],
                ),
        ),
    );

    constructor() {
        addIcons({ notificationsOutline });
    }

    protected goBack(): void {
        this.location.back();
    }
}
